// btw-front/src/components/admin/AdminDealForm.jsx
//
// 관리자 딜 등록/수정 공통 폼.
// - 회사명: 관리자 본인 name (읽기 전용 표시 — DB 의 partner_id 는 NULL)
// - 영업대표: admin_sales_rep 풀에서 셀렉트, 선택 시 phone/email 자동 표시
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/ko_KR";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { CompanySearchSelect } from "../ui/CompanySearchSelect";
import { formatPhone, formatAmount, parseAmount, formatQuarter } from "../../lib/format";
import { listSalesReps } from "../../api/adminSalesReps";
import { uploadQuotation, downloadQuotation } from "../../api/adminDeals";

// yyyy-MM-dd → "yyyyQn" (1~4). 빈 문자열 / 잘못된 입력 시 "" 반환.
export function quarterFromDate(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return "";
  const m = yyyyMmDd.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (!m) return "";
  const month = Number(m[2]);
  if (month < 1 || month > 12) return "";
  const q = Math.floor((month - 1) / 3) + 1;
  return `${m[1]}Q${q}`;
}

export function emptyForm() {
  return {
    projectName: "",
    customerCompany: "",
    customerBusinessNumber: "",
    customerContactName: "",
    customerContactPosition: "",
    customerPhone: "",
    customerEmail: "",
    adminSalesRepId: "",
    poExpectedDate: "",
    productDetail: "",
    poAmount: 0,
    vatExcluded: true,
    salesProgress: "",
    channel: "",
    pocRequired: "undecided",
    brochureDelivered: false,
  };
}

export default function AdminDealForm({
  form,
  setForm,
  adminCompany,
  representativeName,
  submitting,
  onSubmit,
  cancelTo,
  onCancel,
  submitLabel,
  excludeDealId,
  dealId,
  quotation,
}) {
  const { t } = useTranslation();
  const [salesReps, setSalesReps] = useState([]);
  const [salesRepsLoading, setSalesRepsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listSalesReps()
      .then((rows) => {
        if (!cancelled) setSalesReps(rows);
      })
      .catch(() => {
        if (!cancelled) setSalesReps([]);
      })
      .finally(() => {
        if (!cancelled) setSalesRepsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const selectedRep = salesReps.find((r) => String(r.id) === String(form.adminSalesRepId));
  const autoQuarter = quarterFromDate(form.poExpectedDate);

  return (
    <form className="mt-6 grid gap-6" onSubmit={onSubmit} noValidate>
      <Section title={t("partners.deals.form.projectName")}>
        <Input
          label={t("partners.deals.form.projectName")}
          required
          value={form.projectName}
          onChange={(e) => setField("projectName", e.target.value)}
          maxLength={200}
        />
      </Section>

      <Section title={t("partners.deals.form.sectionCustomer")}>
        <div className="grid gap-4 md:grid-cols-2">
          <CompanySearchSelect
            label={t("partners.deals.form.customerCompany")}
            required
            value={form.customerCompany}
            onChange={(name) => setField("customerCompany", name)}
            businessNumber={form.customerBusinessNumber}
            onBusinessNumberChange={(v) => setField("customerBusinessNumber", v)}
            excludeDealId={excludeDealId}
          />
          <Input
            label={t("partners.deals.form.customerContactName")}
            required
            value={form.customerContactName}
            onChange={(e) => setField("customerContactName", e.target.value)}
            maxLength={50}
          />
          <Input
            label={t("partners.deals.form.customerContactPosition")}
            value={form.customerContactPosition}
            onChange={(e) => setField("customerContactPosition", e.target.value)}
            maxLength={100}
          />
          <Input
            label={t("partners.deals.form.customerPhone")}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            value={form.customerPhone}
            onChange={(e) => setField("customerPhone", formatPhone(e.target.value))}
            maxLength={13}
          />
          <Input
            label={t("partners.deals.form.customerEmail")}
            type="email"
            value={form.customerEmail}
            onChange={(e) => setField("customerEmail", e.target.value)}
            maxLength={120}
          />
        </div>
      </Section>

      <Section title={t("admin.deals.sectionRegistrant")}>
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label={t("admin.deals.adminCompany")}
            value={adminCompany || "-"}
            disabled
            tabIndex={-1}
            inputClassName="bg-slate-100 cursor-not-allowed"
          />
          <Input
            label={t("admin.deals.representativeName")}
            value={representativeName || "-"}
            disabled
            tabIndex={-1}
            inputClassName="bg-slate-100 cursor-not-allowed"
          />
        </div>
      </Section>

      <Section title={t("admin.deals.sectionSalesRep")}>
        <div className="grid gap-4 md:grid-cols-2">
          {/* row 1: 이름 셀렉트 / 직책 readonly */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-slate-700">
              {t("admin.deals.salesRepName")} <span className="text-rose-500">*</span>
            </label>
            <select
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/30"
              value={form.adminSalesRepId}
              onChange={(e) => setField("adminSalesRepId", e.target.value)}
              disabled={salesRepsLoading || salesReps.length === 0}
            >
              <option value="">{t("admin.salesReps.selectPlaceholder")}</option>
              {salesReps.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            {salesReps.length === 0 && !salesRepsLoading ? (
              <p className="text-xs text-rose-600">
                {t("admin.salesReps.emptyHint")}{" "}
                <Link to="/admin/sales-reps" className="underline">
                  {t("nav.adminSalesReps")}
                </Link>
              </p>
            ) : (
              <p className="text-xs text-slate-500">{t("admin.salesReps.selectHint")}</p>
            )}
          </div>
          <Input
            label={t("admin.salesReps.position")}
            value={selectedRep?.title || ""}
            disabled
            tabIndex={-1}
            inputClassName="bg-slate-100 cursor-not-allowed"
          />

          {/* row 2: 전화 / 이메일 readonly */}
          <Input
            label={t("admin.deals.salesRepPhone")}
            value={selectedRep?.phone || ""}
            disabled
            tabIndex={-1}
            inputClassName="bg-slate-100 cursor-not-allowed"
          />
          <Input
            label={t("admin.deals.salesRepEmail")}
            value={selectedRep?.email || ""}
            disabled
            tabIndex={-1}
            inputClassName="bg-slate-100 cursor-not-allowed"
          />
        </div>
      </Section>

      <Section title={t("partners.deals.form.sectionDeal")}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-slate-700">
              {t("partners.deals.form.poDate")}
            </label>
            <DatePicker
              className="!h-[38px]"
              format="YYYY-MM-DD"
              locale={locale}
              value={form.poExpectedDate ? dayjs(form.poExpectedDate) : null}
              onChange={(d) => setField("poExpectedDate", d ? d.format("YYYY-MM-DD") : "")}
              placeholder={t("partners.deals.form.poDatePlaceholder")}
            />
          </div>
          <Input
            label={t("partners.deals.form.poQuarter")}
            value={formatQuarter(autoQuarter) || "-"}
            disabled
            tabIndex={-1}
            inputClassName="bg-slate-100 cursor-not-allowed"
            hint={t("partners.deals.form.quarterAuto")}
          />
          <div className="md:col-span-2">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium text-slate-700">
                {t("partners.deals.form.poAmount")}
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-60 rounded-lg border border-slate-300 px-3 py-2 text-right text-sm tabular-nums focus:border-accent focus:ring-2 focus:ring-accent/30"
                  value={formatAmount(form.poAmount)}
                  onChange={(e) => setField("poAmount", parseAmount(e.target.value))}
                  placeholder={t("partners.deals.form.poAmountPlaceholder")}
                />
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/40"
                    checked={form.vatExcluded}
                    onChange={(e) => setField("vatExcluded", e.target.checked)}
                  />
                  {t("partners.deals.form.vatExcluded")}
                </label>
              </div>
            </div>
          </div>
        </div>

        <Textarea
          className="mt-4"
          label={t("partners.deals.form.productDetail")}
          rows={4}
          maxLength={500}
          value={form.productDetail}
          onChange={(e) => setField("productDetail", e.target.value)}
          placeholder={t("partners.deals.form.productDetailPlaceholder")}
        />

        <Textarea
          className="mt-4"
          label={t("partners.deals.form.salesProgress")}
          rows={8}
          maxLength={2000}
          value={form.salesProgress}
          onChange={(e) => setField("salesProgress", e.target.value)}
          placeholder={t("partners.deals.form.salesProgressPlaceholder")}
        />

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Input
            label={t("partners.deals.form.channel")}
            value={form.channel}
            onChange={(e) => setField("channel", e.target.value)}
            maxLength={100}
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-slate-700">
              {t("partners.deals.form.poc")}
            </label>
            <div className="flex gap-2">
              {[
                ["required", t("partners.deals.form.pocRequired")],
                ["not_required", t("partners.deals.form.pocNotRequired")],
                ["undecided", t("partners.deals.form.pocUndecided")],
              ].map(([val, label]) => (
                <label
                  key={val}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                    form.pocRequired === val
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="poc"
                    value={val}
                    className="hidden"
                    checked={form.pocRequired === val}
                    onChange={() => setField("pocRequired", val)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <label className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent/40"
            checked={form.brochureDelivered}
            onChange={(e) => setField("brochureDelivered", e.target.checked)}
          />
          {t("partners.deals.form.brochure")}
        </label>
      </Section>

      {/* 견적서 — 수정 모드(딜 id 존재)에서만. 등록 직후 화면엔 미노출(먼저 저장 필요) */}
      {dealId ? (
        <Section title={t("partners.deals.detail.quotation")}>
          <QuotationField dealId={dealId} initial={quotation} />
        </Section>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {onCancel ? (
          <button type="button" onClick={onCancel} className="btw-btn-ghost">
            {t("partners.deals.detail.cancel")}
          </button>
        ) : (
          <Link to={cancelTo} className="btw-btn-ghost">
            {t("partners.deals.detail.cancel")}
          </Link>
        )}
        <button type="submit" disabled={submitting} className="btw-btn-accent disabled:opacity-60">
          {submitting ? t("partners.deals.form.submitting") : submitLabel}
        </button>
      </div>
    </form>
  );
}

// 견적서 업로드/교체 + 다운로드 (관리자 등록 딜). 폼 저장과 독립된 즉시 액션.
function QuotationField({ dealId, initial }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [info, setInfo] = useState({
    hasQuotation: !!(initial && initial.hasQuotation),
    quotationOriginalName: (initial && initial.quotationOriginalName) || "",
  });
  const [uploading, setUploading] = useState(false);

  async function handlePick(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadQuotation(dealId, file);
      setInfo({
        hasQuotation: true,
        quotationOriginalName: (res && res.quotationOriginalName) || file.name,
      });
      message.success(t("partners.deals.detail.quotationUploaded"));
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("partners.deals.detail.quotationUploadFailed");
      message.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDownload() {
    if (!info.hasQuotation) return;
    try {
      await downloadQuotation(dealId, info.quotationOriginalName);
    } catch (err) {
      message.error((err && err.message) || t("partners.deals.detail.quotationDownloadFailed"));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      {info.hasQuotation ? (
        <button
          type="button"
          onClick={handleDownload}
          className="text-sm font-medium text-accent underline-offset-2 hover:underline"
        >
          {info.quotationOriginalName}
        </button>
      ) : (
        <span className="text-sm text-slate-400">-</span>
      )}
      <button
        type="button"
        disabled={uploading}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-accent hover:text-accent disabled:opacity-60"
      >
        {uploading
          ? t("partners.deals.detail.quotationUploading")
          : info.hasQuotation
            ? t("partners.deals.detail.quotationReplace")
            : t("partners.deals.detail.quotationUpload")}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xlsx,.xls,.doc,.docx,.hwp,.hwpx"
        className="hidden"
        onChange={(e) => handlePick(e.target.files && e.target.files[0])}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
      <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}
